'use client'

import { useMediaQuery, type ButtonProps } from '@relume_io/relume-ui'
import { Button } from '@/components/ui/button'
import { AnimatePresence, motion } from 'framer-motion'
import {
	JSXElementConstructor,
	Key,
	ReactElement,
	ReactNode,
	ReactPortal,
	useState,
} from 'react'
import { RxChevronDown } from 'react-icons/rx'
import logo from '../../../assets/logo.png'
import { useRootLoaderData } from '@/root.tsx'
import {
	DataBackground,
	UiLink,
	UiNavbar,
	UiNavDropdown,
	UiNavLink,
} from '@/types/components'
import { ApiEntityEntity } from '@/types/contentTypes'
import { RootLoaderData } from '@/libs/strapi/rootLoader'
import Background from '@/components/ui/background'
import Image from '@/components/ui/image'
import { GetValue } from '@/types/types'
import { Sub } from '@radix-ui/react-dropdown-menu'
import { AspectRatio } from '@/components/ui/aspect-ratio'

// function extractNavLinks(navbarData: {
// 	navbar_items: Array<{
// 		id: number
// 		title: string
// 		page: { seo: { slug: string } }
// 	}>
// }): NavLink[] {
// 	return navbarData.navbar_items.map((item) => ({
// 		url: item.page.seo.slug,
// 		title: item.title,
// 	}))
// }

export default function Navigation() {
	const rootLoader: RootLoaderData = useRootLoaderData() as RootLoaderData

	// const {
	// 	navbar_items = [],
	// 	navbar_featured_links = [],
	// 	layout_type,
	// 	logo,
	// }: {
	// 	navbar_items: Array<UiNavLink['attributes']>
	// 	navbar_featured_links: Array<UiLink['attributes']>
	// 	layout_type: string
	// 	logo: { url: string; alternativeText: string }
	// } = navigation as UiNavbar['attributes']

	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const isMobile = useMediaQuery('(max-width: 991px)')

	const brand: ApiEntityEntity['attributes'] = rootLoader.brand
	const navigation: UiNavbar['attributes'] = rootLoader.navigation

	const navbar_items: Array<GetValue<UiNavLink | UiNavDropdown>> =
		navigation.navigation_list.nav_items ?? []

	const isLeft = navigation.layout_type === 'logo_left'
	const isCenter = navigation.layout_type === 'logo_center'

	const background: DataBackground['attributes'] = navigation.background
	const backgroundColor = background.background_color

	const isDarkBackground =
		backgroundColor === 'primary' ||
		backgroundColor === 'secondary' ||
		backgroundColor === 'accent' ||
		backgroundColor === 'foreground'
	const logo = isDarkBackground ? (
		<Image image={brand.logo_horizontal_on_dark} className="h-full w-full" />
	) : (
		<Image image={brand.logo_horizontal_on_light} className="h-full w-full" />
	)

	const navbar_featured_links = navigation.navbar_featured_links

	return (
		<Background content={background}>
			<nav className="absolute z-[999] flex min-h-16 w-full items-center border-b-4 border-primary bg-background px-[5%] md:min-h-12">
				<div className="mx-auto size-full min-w-0 max-w-full">
					<div className="flex min-w-0 items-center justify-stretch">
						<div
							id="navLeft"
							className="hidden basis-1/3 items-center justify-start lg:flex"
						>
							{isLeft && <div className="h-12">{logo}</div>}

							{!isLeft && (
								<NavItems navbar_items={navbar_items} isMobile={isMobile} />
							)}
						</div>
						<div
							id="navCenter"
							className="flex basis-1/3 items-center justify-center"
						>
							{isCenter && <div className="h-12">{logo}</div>}
						</div>
						<div
							id="navRight"
							className="flex basis-1/3 items-center justify-end"
						>
							{isLeft && (
								<NavItems navbar_items={navbar_items} isMobile={isMobile} />
							)}
							Locations Switcher
							<div className="flex items-center gap-4">
								{navbar_featured_links.map((button, index) => (
									<Button
										asChild
										key={index}
										variant={index === 0 ? 'default' : 'secondary'}
									>
										<a
											target="_blank"
											href={button.external_url ?? button.page}
										>
											{button.label}
										</a>
									</Button>
								))}
							</div>
						</div>
					</div>
					<button
						className="-mr-2 flex size-12 cursor-pointer flex-col items-center justify-center lg:hidden"
						onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
					>
						<motion.span
							className="my-[3px] h-0.5 w-6 bg-black"
							animate={isMobileMenuOpen ? ['open', 'rotatePhase'] : 'closed'}
							variants={topLineVariants}
						/>
						<motion.span
							className="my-[3px] h-0.5 w-6 bg-black"
							animate={isMobileMenuOpen ? 'open' : 'closed'}
							variants={middleLineVariants}
						/>
						<motion.span
							className="my-[3px] h-0.5 w-6 bg-black"
							animate={isMobileMenuOpen ? ['open', 'rotatePhase'] : 'closed'}
							variants={bottomLineVariants}
						/>
					</button>
				</div>
				{/* <AnimatePresence>
					{isMobileMenuOpen && (
						<motion.div
							variants={{
								open: { height: '100dvh' },
								close: { height: 'auto' },
							}}
							animate={isMobileMenuOpen ? 'open' : 'close'}
							initial="close"
							exit="close"
							className="absolute left-0 right-0 top-full w-full overflow-hidden lg:hidden"
							transition={{ duration: 0.4 }}
						>
							<motion.div
								variants={{
									open: { y: 0 },
									close: { y: '-100%' },
								}}
								animate={isMobileMenuOpen ? 'open' : 'close'}
								initial="close"
								exit="close"
								transition={{ duration: 0.4 }}
								className="border-border-primary absolute left-0 right-0 top-0 block h-[100dvh] overflow-auto border-b bg-background px-[5%] pb-8 pt-4"
							>
								<div className="flex flex-col">
									{navLinks.map((navLink, index) => (
										<div key={index}>
											{navLink.megaMenu ? (
												<SubMenu
													megaMenu={navLink.megaMenu}
													title={navLink.title}
													isMobile={isMobile}
												/>
											) : (
												<a href={navLink.url} className="block py-3 text-md">
													{navLink.title}
												</a>
											)}
										</div>
									))}
									<div className="mt-6 flex flex-col items-stretch gap-4">
										{buttons.map((button, index) => (
											<Button key={index} {...button}>
												{button.title}
											</Button>
										))}
									</div>
								</div>
							</motion.div>
						</motion.div>
					)}
				</AnimatePresence> */}
			</nav>
		</Background>
	)
}

const Logo = ({ logo }: { logo: { url: string; alternativeText: string } }) => {
	if (!logo.url) return null
	return (
		<a href="/home">
			<img src={logo.url} alt={logo.alternativeText} />
		</a>
	)
}

const NavItems = ({
	navbar_items,
	isMobile,
}: {
	navbar_items: Array<UiNavLink['attributes'] | UiNavDropdown['attributes']>
	isMobile: boolean
}) => {
	return (
		<div className="flex flex-col items-center lg:flex-row">
			{navbar_items.map((navLink, index) => {
				if (navLink.__component === 'ui.nav-dropdown') {
					return (
						<div key={`${navLink.__component} ${index}`}>
							<SubMenu dropDown={navLink} isMobile={isMobile} />
							{/* <NavDropdown navDropdown={navLink} isMobile={isMobile} /> */}
						</div>
					)
				}

				if (navLink.__component === 'ui.nav-link') {
					return (
						<NavLink
							navLink={navLink}
							index={index}
							key={`${navLink.__component} ${index}`}
						>
							{navLink.title}
						</NavLink>
					)
				}
			})}
		</div>
	)
}

export function NavLink({
	navLink,
	children,
	index,
}: {
	navLink: UiNavLink['attributes']
	children?: ReactNode
	index: number
}) {
	return (
		<a
			href={navLink.page?.seo?.slug ?? '/'}
			key={`${navLink.__component} ${index}`}
			className="relative block w-auto cursor-pointer border-b-4 border-background py-3 text-sm font-semibold uppercase tracking-[0.2em] text-secondary transition-colors hover:border-primary hover:text-primary lg:inline-block lg:px-3 lg:py-6"
		>
			{children}
		</a>
	)
}

const SubMenu = ({
	dropDown,
	isMobile,
}: {
	dropDown: UiNavDropdown['attributes']
	isMobile: boolean
}) => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	const navLinks = dropDown.nav_links ?? []

	return (
		<div
			onMouseEnter={() => !isMobile && setIsDropdownOpen(true)}
			onMouseLeave={() => !isMobile && setIsDropdownOpen(false)}
		>
			<button
				className={`relative flex w-auto cursor-pointer items-center justify-between gap-1 whitespace-nowrap border-b-4 border-background py-3 text-sm text-secondary transition-colors ${isDropdownOpen && 'border-primary text-primary'} hover:text-primary lg:px-3 lg:py-6`}
				onClick={() => setIsDropdownOpen((prev) => !prev)}
			>
				<span className="text-sm font-semibold uppercase tracking-[0.2em]">
					{dropDown.title}
				</span>
				<motion.span
					animate={isDropdownOpen ? 'rotated' : 'initial'}
					variants={{
						rotated: { rotate: 360 },
						initial: { rotate: 270 },
					}}
					transition={{ duration: 0.3 }}
				>
					<RxChevronDown />
				</motion.span>
			</button>
			<AnimatePresence>
				{isDropdownOpen && (
					<motion.nav
						variants={{
							open: {
								opacity: 1,
								height: 'var(--height-open, auto)',
							},
							close: {
								opacity: 0,
								height: 'var(--height-close, 0)',
							},
						}}
						animate={isDropdownOpen ? 'open' : 'close'}
						initial="close"
						exit="close"
						transition={{ duration: 0.2 }}
						className="bottom-auto left-0 top-full w-full min-w-full max-w-full overflow-hidden bg-primary text-primary-foreground lg:absolute lg:w-screen lg:border-b lg:border-primary lg:px-[5%] lg:[--height-close:auto]"
					>
						<div className="mx-auto flex size-full min-w-0 max-w-full items-center justify-stretch">
							<div className="flex grow gap-4 p-12">
								{/* <h4 className="text-sm font-semibold leading-[1.4] md:leading-[1.3]">
											{megaMenu.linkGroup.title}
										</h4> */}
								{navLinks.map(
									(
										subMenuLink: UiNavLink['attributes'],
										index: Key | null | undefined,
									) => {
										const slug = subMenuLink.page?.seo?.slug ?? ''
										return (
											<a
												key={index}
												className="flex flex-col gap-2 text-sm font-semibold uppercase tracking-[0.2em] lg:basis-1/4"
												href={slug}
											>
												<AspectRatio ratio={1 / 1}>
													<Image
														image={subMenuLink.image}
														className="h-full w-full object-cover object-center"
													/>
												</AspectRatio>
												{subMenuLink.title}
											</a>
										)
									},
								)}
							</div>
						</div>
					</motion.nav>
				)}
			</AnimatePresence>
		</div>
	)
}

const topLineVariants = {
	open: {
		translateY: 8,
		transition: { delay: 0.1 },
	},
	rotatePhase: {
		rotate: -45,
		transition: { delay: 0.2 },
	},
	closed: {
		translateY: 0,
		rotate: 0,
		transition: { duration: 0.2 },
	},
}

const middleLineVariants = {
	open: {
		width: 0,
		transition: { duration: 0.1 },
	},
	closed: {
		width: '1.5rem',
		transition: { delay: 0.3, duration: 0.2 },
	},
}

const bottomLineVariants = {
	open: {
		translateY: -8,
		transition: { delay: 0.1 },
	},
	rotatePhase: {
		rotate: 45,
		transition: { delay: 0.2 },
	},
	closed: {
		translateY: 0,
		rotate: 0,
		transition: { duration: 0.2 },
	},
}
